/**
 * A hierarchical data grid whose reorder is a GOVERNED COMMAND, not a local
 * animation (#1466 recipe 4, ruling 48362).
 *
 * ── the sentence the whole component is built around ────────────────────────
 * *"Drag/drop is only a gesture over one governed `move` command carrying
 * parent/group/order plus expected revision; no client-only reorder truth."*
 *
 * So this grid **never reorders itself**. There is no local copy of the rows,
 * no optimistic splice, no "snap back on failure". A gesture — pointer or
 * keyboard — resolves to one `TreeGridMoveCommand`, that command goes to the
 * host, and the next authoritative `rows` array is what gets rendered. The
 * screen is therefore never showing an order the server has not agreed to.
 *
 * The defect this prevents is not cosmetic. An optimistic tree that guesses
 * wrong shows a person a hierarchy that does not exist, they take a second
 * decision on the strength of it, and the correction arrives after the damage.
 * A snap-back animation is an admission that this has already happened once.
 *
 * ── what "improving on the donor" means here ────────────────────────────────
 * The donor surface (#1370 comment 48368) was honest about itself and poor all
 * the same: it is not actually drag/drop at all, it renders tiny
 * up/down/promote/demote buttons over governed move functions. Governed —
 * right. Tiny buttons as the ONLY gesture — wrong, and under the ruled target
 * floor besides.
 *
 * The improvement keeps the governed move exactly as it was and gives it TWO
 * first-class gestures that emit the identical command:
 *
 *   · pointer drag, with drop targets validated BEFORE the drop and told apart
 *     by shape as well as colour;
 *   · `Alt` + arrows, which reorder and reparent from the keyboard and ANNOUNCE
 *     the result. Not a fallback: §5 of the test file drives the entire
 *     reorder/reparent contract through the keyboard alone, so if the pointer
 *     path were deleted the component would still be completely usable.
 *
 * Both call `commitMove`. There is one code path from gesture to command, which
 * is the only way the two can be guaranteed to mean the same thing.
 *
 * ── CONTROLLED, like the sibling rail ───────────────────────────────────────
 * `body`, `expandedIds`, `selection` and `revision` are all the host's. This
 * component reads no route, no `localStorage`, no global context, and holds no
 * state a reload could contradict. Its only internal state is transient
 * interaction state — which row has roving focus, which row is mid-drag, which
 * move is in flight, and the text of the last announcement. None of that is
 * data; all of it dies with the mount, which is correct.
 *
 * ── identity, and why focus and selection are keyed by `id` ─────────────────
 * Rows are React-keyed by `row.id` and focus is remembered as an `id`, never as
 * an index. After a move the array is a different array in a different order;
 * an index-keyed grid hands the moved row's DOM node, focus ring and selection
 * highlight to whichever row now occupies slot 4. Keying by id means the
 * person's focus follows the row they just moved — which is also what makes the
 * CONFLICT case survivable.
 *
 * ── the CONFLICT case, modelled rather than implied ─────────────────────────
 * `expectedRevision` means the host can answer `{ kind: 'conflict' }`. Because
 * nothing was ever reordered locally, "restore the authoritative order" is
 * already true by construction — but the two things that would still be lost
 * are handled explicitly, because implied behaviour is untested behaviour:
 *
 *   · SELECTION is the host's prop and is never touched by a refused, conflicted
 *     or errored move. The grid does not clear a selection because a command
 *     failed; the person's chosen rows are not the server's to revoke.
 *   · FOCUS is restored to the row the person tried to move, AFTER the new
 *     authoritative rows have rendered, via `restoreFocusTo`.
 *
 * ── the bulk selection shape ────────────────────────────────────────────────
 * `TreeGridSelection` distinguishes `{ kind: 'ids' }` from
 * `{ kind: 'all', excluded }`. That is the difference between "the 50 rows you
 * loaded" and "the 12,480 rows the query matches, minus three", and collapsing
 * them is the classic governed-bulk defect: the header checkbox says "all", the
 * act reaches one page, and nobody notices until the other 12,430 are still
 * sitting there. Deselecting a row under `all` therefore produces an EXCLUSION
 * and stays `all` — see `toggleSelection`.
 *
 * ── finite body states ──────────────────────────────────────────────────────
 * The same six as the sibling rail, for the same reason: REFUSED is not EMPTY.
 * A hierarchy is exactly where a governed read gets partially withheld, and
 * "there is nothing here" is a lie that sends someone off to create a duplicate
 * of the thing they were merely not allowed to see.
 *
 * ── deliberately out of scope ───────────────────────────────────────────────
 * Cell-level keyboard navigation. `role="treegrid"` permits it, but the rows
 * here are records and the cells are read-only projections; a second navigation
 * axis would cost every keyboard user extra keystrokes per row to buy a
 * capability this recipe has no use for. Rows are the navigable unit. If a
 * consumer needs cell navigation it is a new slice, and it should arrive with
 * the measurement that justifies it.
 */
import {
  useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { readableError } from '@solum/format'
import s from './TreeGrid.module.css'

/* What a refusal reads as when the host's own words were machine text (#3012). */
const REFUSED = 'You don’t have access to this.'

/* ───────────────────────────── the data contract ───────────────────────── */

export interface TreeGridRow<T = unknown> {
  /** Stable across reorders. Focus, selection and every move speak in these. */
  id: string
  /** `null` = a top-level row. An id nobody sent is treated as `null`; see below. */
  parentId: string | null
  /** The spoken name — announcements, and the row's accessible name. */
  label: string
  /** Whatever the columns need. The grid never inspects it. */
  data: T
  /** May contain children. Decides whether "drop inside" and indent are legal. */
  container?: boolean
  /**
   * The host knows there are children it has not sent yet. The expander is
   * offered anyway, because a lazily-loaded group that looks like a leaf is a
   * group nobody ever opens.
   */
  hasChildren?: boolean
  /** `false` = pinned. Handle withheld, and the keyboard is refused WITH A REASON. */
  moveable?: boolean
  /** `false` = not eligible for selection or for a bulk act. */
  selectable?: boolean
}

export interface TreeGridColumn<T = unknown> {
  key: string
  header: string
  /** Any CSS grid track. Defaults to `minmax(0, 1fr)`. */
  width?: string
  align?: 'start' | 'end'
  /** Withheld unless held. Omitted = always shown. `null` permitted = no model. */
  capability?: string
  cell: (row: TreeGridRow<T>) => ReactNode
}

/**
 * The six states a grid body can be in, made finite so a host cannot invent a
 * seventh and so REFUSED cannot be rendered as EMPTY.
 */
export type TreeGridBody<T = unknown> =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: readonly TreeGridRow<T>[] }
  | { kind: 'empty'; message: string }
  /** Part of the hierarchy arrived. The part that did is still worth showing. */
  | { kind: 'partial'; rows: readonly TreeGridRow<T>[]; message: string }
  /** Authorisation said no. Say so, and say what it was. */
  | { kind: 'refused'; message: string }
  | { kind: 'error'; message: string }

/* ────────────────────────────── the move command ───────────────────────── */

/**
 * The one governed command. Every gesture produces exactly this.
 *
 * `index` is the position among the DESTINATION PARENT'S CHILDREN AFTER THE
 * MOVED ROW HAS BEEN REMOVED. That convention has to be written down, because
 * the alternative ("the index in the list as it currently looks") is off by one
 * for every downward move within a parent — and an off-by-one in a reorder is a
 * silent no-op that people repeat four times before they report it.
 */
export interface TreeGridMoveCommand {
  id: string
  parentId: string | null
  index: number
  /** The `revision` the grid was rendering. The host's concurrency token. */
  expectedRevision: string | number
}

export type TreeGridMoveResult =
  | { kind: 'accepted'; message?: string }
  /** Authorisation said no. Nothing moved, and nothing local pretended it had. */
  | { kind: 'refused'; message: string }
  /** The revision moved under us. What is rendered is already authoritative. */
  | { kind: 'conflict'; message: string }
  | { kind: 'error'; message: string }

/* ─────────────────────────────── selection ─────────────────────────────── */

export type TreeGridSelection =
  | { kind: 'none' }
  | { kind: 'ids'; ids: readonly string[] }
  /**
   * Everything the current governed query matches — including rows this client
   * has never loaded. `count` is the host's number, or `null` when it does not
   * know one. `excluded` are deselections layered on top.
   */
  | { kind: 'all'; count: number | null; excluded: readonly string[] }

export function isRowSelected(selection: TreeGridSelection, id: string): boolean {
  switch (selection.kind) {
    case 'none': return false
    case 'ids': return selection.ids.includes(id)
    case 'all': return !selection.excluded.includes(id)
  }
}

/**
 * Toggling under `all` produces an EXCLUSION and stays `all`.
 *
 * Collapsing it to the loaded ids is the defect described in the header: the
 * person asked for "everything matching", unticked three, and would silently
 * get "the fifty on screen minus three" instead.
 */
export function toggleSelection(selection: TreeGridSelection, id: string): TreeGridSelection {
  if (selection.kind === 'all') {
    const excluded = selection.excluded.includes(id)
      ? selection.excluded.filter((x) => x !== id)
      : [...selection.excluded, id]
    return { kind: 'all', count: selection.count, excluded }
  }
  const ids = selection.kind === 'ids' ? selection.ids : []
  return ids.includes(id)
    ? { kind: 'ids', ids: ids.filter((x) => x !== id) }
    : { kind: 'ids', ids: [...ids, id] }
}

/** What a bulk act would actually reach, said in words rather than a number. */
export function selectionSummary(selection: TreeGridSelection, loaded: number): string {
  switch (selection.kind) {
    case 'none': return 'None selected'
    case 'ids': return `${selection.ids.length} of ${loaded} selected`
    case 'all': {
      const base = selection.count === null
        ? 'All matching rows selected'
        : `All ${selection.count} matching rows selected`
      return selection.excluded.length === 0
        ? base
        : `${base}, except ${selection.excluded.length}`
    }
  }
}

/* ───────────────────────── pure tree + move resolution ─────────────────── */

export interface TreeGridVisibleRow<T = unknown> {
  row: TreeGridRow<T>
  /** 1-based, for `aria-level`. */
  level: number
  posinset: number
  setsize: number
}

/**
 * The effective parent of a row.
 *
 * A `parentId` nobody sent, and a `parentId` cycle, both resolve to `null` — so
 * the row is shown at the top level instead of disappearing. Dropping a row
 * because its parent link is inconsistent is the worst available answer: the
 * data is already wrong, and now nobody can see the row that would let them fix
 * it. The hop counter is what makes a cycle terminate.
 */
function effectiveParentId<T>(
  row: TreeGridRow<T>, byId: ReadonlyMap<string, TreeGridRow<T>>, size: number,
): string | null {
  if (!row.parentId || !byId.has(row.parentId)) return null
  let hops = 0
  let cur: TreeGridRow<T> | undefined = byId.get(row.parentId)
  while (cur?.parentId && byId.has(cur.parentId)) {
    if (++hops > size) return null
    cur = byId.get(cur.parentId)
  }
  return row.parentId
}

/** Children of `parentId`, in the authoritative array's own order. */
export function treeChildren<T>(
  rows: readonly TreeGridRow<T>[], parentId: string | null,
): readonly TreeGridRow<T>[] {
  const byId = new Map(rows.map((r) => [r.id, r]))
  return rows.filter((r) => effectiveParentId(r, byId, rows.length) === parentId)
}

/** Depth-first over the rows whose every ancestor is expanded. */
export function visibleTreeRows<T>(
  rows: readonly TreeGridRow<T>[], expandedIds: readonly string[],
): TreeGridVisibleRow<T>[] {
  const byId = new Map(rows.map((r) => [r.id, r]))
  const byParent = new Map<string | null, TreeGridRow<T>[]>()
  for (const row of rows) {
    const parent = effectiveParentId(row, byId, rows.length)
    const bucket = byParent.get(parent)
    if (bucket) bucket.push(row)
    else byParent.set(parent, [row])
  }
  const open = new Set(expandedIds)
  const out: TreeGridVisibleRow<T>[] = []
  const seen = new Set<string>()
  const walk = (parent: string | null, level: number) => {
    const kids = byParent.get(parent) ?? []
    kids.forEach((row, i) => {
      if (seen.has(row.id)) return /* a duplicate id: render it once, not twice */
      seen.add(row.id)
      out.push({ row, level, posinset: i + 1, setsize: kids.length })
      if (open.has(row.id)) walk(row.id, level + 1)
    })
  }
  walk(null, 1)
  return out
}

/** Is `id` inside `ancestorId`'s subtree? The core drop-validity question. */
export function isDescendantOf<T>(
  rows: readonly TreeGridRow<T>[], ancestorId: string, id: string,
): boolean {
  const byId = new Map(rows.map((r) => [r.id, r]))
  let cur = byId.get(id)
  let hops = 0
  while (cur && hops++ <= rows.length) {
    const parent = effectiveParentId(cur, byId, rows.length)
    if (parent === ancestorId) return true
    cur = parent ? byId.get(parent) : undefined
  }
  return false
}

export type TreeGridDropPosition = 'before' | 'inside' | 'after'

export type MoveResolution =
  | { kind: 'move'; parentId: string | null; index: number }
  /** The gesture landed where the row already is. A command would be noise. */
  | { kind: 'noop' }
  | { kind: 'invalid'; reason: string }

/**
 * Which third of a row the pointer is in.
 *
 * 28 / 72 rather than a symmetric 33 / 66: the INSIDE band gets 44% of the row
 * because reparenting is the gesture people miss, and on the ruled 44px row
 * that is ≈19px of target against ≈12px for each edge band. A row that cannot
 * contain children has no inside band at all — offering one and then refusing
 * the drop is how people learn to distrust the highlight.
 *
 * An UNMEASURABLE pointer resolves to `before`, the least destructive of the
 * three. Both halves of that guard were paid for: `height <= 0` is an
 * unlaid-out row, and a non-finite `offsetY` is a drag event that carried no
 * coordinate at all — which is exactly what jsdom produces, and produced a
 * silent `inside` here until the guard was added. A reparent is the one answer
 * that must never come from arithmetic on a number nobody supplied.
 */
export function dropZone(
  offsetY: number, height: number, canContain: boolean,
): TreeGridDropPosition {
  if (!(height > 0) || !Number.isFinite(offsetY)) return 'before'
  const t = offsetY / height
  if (!canContain) return t < 0.5 ? 'before' : 'after'
  if (t < 0.28) return 'before'
  if (t > 0.72) return 'after'
  return 'inside'
}

/** Where a drop onto `overId` would put `dragId`, or why it would not. */
export function resolveDrop<T>(
  rows: readonly TreeGridRow<T>[], dragId: string, overId: string,
  position: TreeGridDropPosition,
): MoveResolution {
  const byId = new Map(rows.map((r) => [r.id, r]))
  const drag = byId.get(dragId)
  const over = byId.get(overId)
  if (!drag || !over) return { kind: 'invalid', reason: 'That row is no longer here.' }
  if (drag.moveable === false) return { kind: 'invalid', reason: `${drag.label} cannot be moved.` }
  if (dragId === overId) return { kind: 'invalid', reason: 'A row cannot be moved onto itself.' }
  if (isDescendantOf(rows, dragId, overId)) {
    return { kind: 'invalid', reason: `${drag.label} cannot be moved inside its own children.` }
  }
  if (position === 'inside' && !over.container) {
    return { kind: 'invalid', reason: `${over.label} cannot contain rows.` }
  }
  const parentId = position === 'inside' ? overId : effectiveParentId(over, byId, rows.length)
  return place(rows, dragId, parentId, position === 'inside' ? null : overId, position)
}

export type TreeGridMoveGesture = 'up' | 'down' | 'indent' | 'outdent'

/**
 * The keyboard's four gestures, resolved against the rules the pointer uses.
 *
 * `indent` nests a row under the sibling above it. `outdent` lifts it out of
 * its parent and places it immediately AFTER that parent, which is where people
 * expect it to land and the placement that keeps the subtree reading in order —
 * appending it to the end of the grandparent instead is technically a
 * promotion and practically a disappearance.
 */
export function resolveKeyboardMove<T>(
  rows: readonly TreeGridRow<T>[], id: string, gesture: TreeGridMoveGesture,
): MoveResolution {
  const byId = new Map(rows.map((r) => [r.id, r]))
  const row = byId.get(id)
  if (!row) return { kind: 'invalid', reason: 'That row is no longer here.' }
  if (row.moveable === false) return { kind: 'invalid', reason: `${row.label} cannot be moved.` }

  const parentId = effectiveParentId(row, byId, rows.length)
  const siblings = treeChildren(rows, parentId)
  const at = siblings.findIndex((r) => r.id === id)

  switch (gesture) {
    case 'up':
      if (at <= 0) return { kind: 'invalid', reason: `${row.label} is already first.` }
      return { kind: 'move', parentId, index: at - 1 }
    case 'down':
      if (at === siblings.length - 1) {
        return { kind: 'invalid', reason: `${row.label} is already last.` }
      }
      return { kind: 'move', parentId, index: at + 1 }
    case 'indent': {
      if (at <= 0) {
        return { kind: 'invalid', reason: `${row.label} has nothing above it to nest under.` }
      }
      const host = siblings[at - 1]!
      if (!host.container) return { kind: 'invalid', reason: `${host.label} cannot contain rows.` }
      return place(rows, id, host.id, null, 'after')
    }
    case 'outdent': {
      if (parentId === null) {
        return { kind: 'invalid', reason: `${row.label} is already at the top level.` }
      }
      const parent = byId.get(parentId)!
      return place(rows, id, effectiveParentId(parent, byId, rows.length), parentId, 'after')
    }
  }
}

/**
 * The shared placement arithmetic, and the reason `index` means what the
 * `TreeGridMoveCommand` doc says it means: the destination siblings are counted
 * WITHOUT the moved row, so the same number is correct whichever direction the
 * row travelled from.
 */
function place<T>(
  rows: readonly TreeGridRow<T>[], dragId: string,
  parentId: string | null, refId: string | null, position: TreeGridDropPosition,
): MoveResolution {
  const byId = new Map(rows.map((r) => [r.id, r]))
  const drag = byId.get(dragId)
  if (!drag) return { kind: 'invalid', reason: 'That row is no longer here.' }
  const currentParent = effectiveParentId(drag, byId, rows.length)
  const currentIndex = treeChildren(rows, currentParent).findIndex((r) => r.id === dragId)

  const siblings = treeChildren(rows, parentId).filter((r) => r.id !== dragId)
  let index: number
  if (refId === null) index = siblings.length
  else {
    const ref = siblings.findIndex((r) => r.id === refId)
    if (ref === -1) return { kind: 'invalid', reason: 'That position is no longer here.' }
    index = position === 'before' ? ref : ref + 1
  }
  if (parentId === currentParent && index === currentIndex) return { kind: 'noop' }
  return { kind: 'move', parentId, index }
}

/* ───────────────────────────── capability ──────────────────────────────── */

/** `null` permitted = "this host has no capability model", NOT "nothing permitted". */
export function permittedColumns<T>(
  columns: readonly TreeGridColumn<T>[], permitted: readonly string[] | null | undefined,
): readonly TreeGridColumn<T>[] {
  if (permitted == null) return columns
  const held = new Set(permitted)
  return columns.filter((c) => !c.capability || held.has(c.capability))
}

/* ───────────────────────────── the component ───────────────────────────── */

export interface TreeGridProps<T = unknown> {
  /** The spoken name of the grid — "Assembly structure". */
  label: string
  columns: readonly TreeGridColumn<T>[]
  body: TreeGridBody<T>
  /**
   * The revision of the hierarchy being rendered. Carried on every move as
   * `expectedRevision`, which is what lets the host answer `conflict`.
   */
  revision: string | number
  expandedIds: readonly string[]
  onExpandedChange: (ids: readonly string[]) => void
  selection?: TreeGridSelection
  /** Omitted = the grid is not selectable at all: no boxes, no bulk bar. */
  onSelectionChange?: (next: TreeGridSelection) => void
  /** How many rows the governed query matches in total, for the bulk escalation. */
  totalCount?: number | null
  /** Capability keys held, or `null` for "no capability model". */
  permitted?: readonly string[] | null
  /** Needed to move anything. Omitted = moving needs no capability. */
  moveCapability?: string
  /** Omitted = a read-only hierarchy: no handles, no `Alt`+arrows, no commands. */
  onMove?: (command: TreeGridMoveCommand) => TreeGridMoveResult | Promise<TreeGridMoveResult>
  onActivate?: (id: string) => void
  testId?: string
}

const NO_SELECTION: TreeGridSelection = { kind: 'none' }

export function TreeGrid<T>({
  label, columns, body, revision, expandedIds, onExpandedChange,
  selection = NO_SELECTION, onSelectionChange, totalCount = null,
  permitted = null, moveCapability, onMove, onActivate, testId = 'treegrid',
}: TreeGridProps<T>) {
  const uid = useId()
  const gridRef = useRef<HTMLDivElement>(null)

  const cols = useMemo(() => permittedColumns(columns, permitted), [columns, permitted])
  const rows = body.kind === 'ready' || body.kind === 'partial' ? body.rows : []
  const visible = useMemo(() => visibleTreeRows(rows, expandedIds), [rows, expandedIds])

  /* Withheld outright rather than rendered dead: a handle that refuses every
     drop reads as a broken grid rather than a governed one. Row-level
     `moveable: false` is the different case — that one answers WITH A REASON
     when the keyboard asks, because a pinned row inside a movable tree is a
     rule the person needs to be told rather than left to infer. */
  const mayMove = onMove != null
    && (permitted == null || !moveCapability || permitted.includes(moveCapability))

  /* ── transient interaction state; none of it is data ─────────────────────── */
  const [focusId, setFocusId] = useState<string | null>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  const [drop, setDrop] = useState<
  { overId: string; position: TreeGridDropPosition; invalid?: string } | null>(null)
  const [movingId, setMovingId] = useState<string | null>(null)
  const [status, setStatus] = useState({ text: '', n: 0 })
  const [alert, setAlert] = useState({ text: '', n: 0 })
  const announce = useCallback((text: string) => setStatus((p) => ({ text, n: p.n + 1 })), [])
  const warn = useCallback((text: string) => setAlert((p) => ({ text, n: p.n + 1 })), [])

  /* Focus is remembered as an id and re-applied AFTER the authoritative rows
     have rendered — the whole point, since a move (or a conflict) hands us a
     different array and an index would now be pointing at a stranger. */
  const restoreFocusTo = useRef<string | null>(null)
  useLayoutEffect(() => {
    const want = restoreFocusTo.current
    if (!want) return
    const el = gridRef.current?.querySelector<HTMLDivElement>(`[data-row-id="${want}"]`)
    if (!el) return /* keep waiting: the row may arrive with the next revision */
    restoreFocusTo.current = null
    setFocusId(want)
    el.focus()
  })

  /* A remembered focus on a row that has gone would strand the grid with no tab
     stop at all, because the roving tabindex would find no owner. */
  useEffect(() => {
    if (focusId !== null && !visible.some((v) => v.row.id === focusId)) setFocusId(null)
  }, [focusId, visible])

  /* ── the one path from any gesture to the governed command ───────────────── */
  const commitMove = useCallback(async (id: string, resolution: MoveResolution) => {
    if (resolution.kind === 'noop') return
    if (resolution.kind === 'invalid') { warn(resolution.reason); return }
    if (!onMove || !mayMove) return
    /* One move at a time. A second command computed against a tree the first has
       already invalidated would carry a stale index, and the host would apply it
       to a hierarchy that is not the one the person was looking at. */
    if (movingId !== null) return

    setMovingId(id)
    let result: TreeGridMoveResult
    try {
      result = await onMove({
        id, parentId: resolution.parentId, index: resolution.index, expectedRevision: revision,
      })
    } catch (cause) {
      result = {
        kind: 'error',
        message: readableError(cause, 'The move could not be completed.'),
      }
    }
    setMovingId(null)
    /* Focus follows the row in EVERY outcome, including the ones where nothing
       moved. Being dumped at the top of the grid after a refusal is a second
       punishment for a thing that was not the person's fault. */
    restoreFocusTo.current = id

    switch (result.kind) {
      case 'accepted': {
        const where = resolution.parentId === null
          ? 'at the top level'
          : `under ${rowLabel(rows, resolution.parentId)}`
        announce(result.message
          ?? `${rowLabel(rows, id)} moved to position ${resolution.index + 1} ${where}.`)
        break
      }
      case 'conflict':
        /* Nothing local was ever reordered, so the order on screen is already
           the authoritative one. Saying so out loud matters: "it did not move"
           and "it moved and came back" look identical, and only one of them
           means "look again before you retry". */
        warn(`${readableError(result.message, 'That row changed in the meantime.')} Nothing was moved; the order shown is the current one.`)
        break
      case 'refused':
      case 'error':
        warn(readableError(result.message, 'The move could not be completed.'))
        break
    }
  }, [onMove, mayMove, movingId, revision, rows, announce, warn])

  /* ── expansion and selection, both the host's ────────────────────────────── */
  const setExpanded = (id: string, next: boolean) => {
    const set = new Set(expandedIds)
    if (next) set.add(id)
    else set.delete(id)
    onExpandedChange([...set])
  }
  const toggleRow = (id: string) => onSelectionChange?.(toggleSelection(selection, id))

  const selectableIds = visible.filter((v) => v.row.selectable !== false).map((v) => v.row.id)
  const allLoadedSelected = selectableIds.length > 0
    && selectableIds.every((id) => isRowSelected(selection, id))

  /* ── keyboard ────────────────────────────────────────────────────────────── */
  const focusAt = (index: number) => {
    const target = visible[Math.max(0, Math.min(visible.length - 1, index))]
    if (!target) return
    setFocusId(target.row.id)
    gridRef.current?.querySelector<HTMLDivElement>(`[data-row-id="${target.row.id}"]`)?.focus()
  }

  const isBranch = (row: TreeGridRow<T>) =>
    row.container === true || row.hasChildren === true || rows.some((r) => r.parentId === row.id)

  const onRowKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>, v: TreeGridVisibleRow<T>) => {
    const i = visible.indexOf(v)
    const row = v.row
    const open = expandedIds.includes(row.id)
    const branch = isBranch(row)

    /* `Alt` is checked FIRST. Bare arrows navigate, modified arrows move — the
       pairing people already know from every outliner they have used, and it
       keeps the reorder gesture off the keys a screen-reader user holds down to
       read the tree. */
    if (event.altKey && !event.ctrlKey && !event.metaKey) {
      const gesture: TreeGridMoveGesture | null =
        event.key === 'ArrowUp' ? 'up'
          : event.key === 'ArrowDown' ? 'down'
            : event.key === 'ArrowRight' ? 'indent'
              : event.key === 'ArrowLeft' ? 'outdent'
                : null
      if (gesture === null) return
      event.preventDefault()
      if (!mayMove) { warn('You cannot reorder these rows.'); return }
      void commitMove(row.id, resolveKeyboardMove(rows, row.id, gesture))
      return
    }

    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); focusAt(i + 1); return
      case 'ArrowUp': event.preventDefault(); focusAt(i - 1); return
      case 'Home': event.preventDefault(); focusAt(0); return
      case 'End': event.preventDefault(); focusAt(visible.length - 1); return
      case 'ArrowRight':
        event.preventDefault()
        if (branch && !open) setExpanded(row.id, true)
        else if (branch && open) focusAt(i + 1)
        return
      case 'ArrowLeft': {
        event.preventDefault()
        if (branch && open) { setExpanded(row.id, false); return }
        const parent = visible.findIndex((x) => x.row.id === row.parentId)
        if (parent >= 0) focusAt(parent)
        return
      }
      case ' ':
        if (row.selectable === false || !onSelectionChange) return
        event.preventDefault(); toggleRow(row.id)
        return
      case 'Enter':
        if (!onActivate) return
        event.preventDefault(); onActivate(row.id)
        return
      default:
    }
  }

  /* ── pointer drag, over the SAME resolution the keyboard uses ────────────── */
  const onDragOver = (event: ReactDragEvent<HTMLDivElement>, v: TreeGridVisibleRow<T>) => {
    if (dragId === null) return
    const rect = event.currentTarget.getBoundingClientRect()
    const position = dropZone(event.clientY - rect.top, rect.height, v.row.container === true)
    const resolution = resolveDrop(rows, dragId, v.row.id, position)
    if (resolution.kind === 'invalid') {
      setDrop({ overId: v.row.id, position, invalid: resolution.reason })
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'none'
      return /* deliberately NOT prevented: the browser then refuses the drop */
    }
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    setDrop({ overId: v.row.id, position })
  }

  const onDropRow = (event: ReactDragEvent<HTMLDivElement>, v: TreeGridVisibleRow<T>) => {
    event.preventDefault()
    const id = dragId
    const position = drop?.overId === v.row.id ? drop.position : 'before'
    setDragId(null)
    setDrop(null)
    if (id === null) return
    void commitMove(id, resolveDrop(rows, id, v.row.id, position))
  }

  const headId = `${testId}-head-${uid}`
  const tracks = { gridTemplateColumns: cols.map((c) => c.width ?? 'minmax(0, 1fr)').join(' ') }

  return (
    <div className={s.root} data-testid={testId}>
      {onSelectionChange && (
        <div className={s.bulk} data-testid={`${testId}-bulk`}>
          <input
            type="checkbox"
            className={s.check}
            checked={allLoadedSelected}
            disabled={selectableIds.length === 0}
            /* This box speaks for the LOADED rows only, and says so. Escalating
               to "everything matching" is a separate, deliberate act. */
            aria-label={`Select the ${selectableIds.length} loaded rows`}
            data-testid={`${testId}-select-loaded`}
            onChange={(e) => onSelectionChange(
              e.currentTarget.checked ? { kind: 'ids', ids: selectableIds } : { kind: 'none' },
            )}
          />
          <span className={s.bulkCount} data-testid={`${testId}-selection-summary`}>
            {selectionSummary(selection, selectableIds.length)}
          </span>
          {/* Offered only when there genuinely is more than is on screen, and
              never performed implicitly: an act over rows nobody has seen is the
              one act that has to be asked for in so many words. */}
          {selection.kind === 'ids' && selection.ids.length > 0
            && totalCount !== null && totalCount > selectableIds.length && (
            <button
              type="button"
              className={s.escalate}
              data-testid={`${testId}-select-all-matching`}
              onClick={() => onSelectionChange({ kind: 'all', count: totalCount, excluded: [] })}
            >
              Select all {totalCount} matching rows
            </button>
          )}
        </div>
      )}

      <div
        ref={gridRef}
        className={s.grid}
        role="treegrid"
        aria-label={label}
        aria-rowcount={visible.length + 1}
        aria-busy={movingId !== null || body.kind === 'loading'}
        data-testid={`${testId}-grid`}
      >
        <div role="rowgroup" className={s.head}>
          <div role="row" className={s.hrow} id={headId} aria-rowindex={1} style={tracks}>
            {cols.map((c, i) => (
              <div
                key={c.key}
                role="columnheader"
                className={s.hcell}
                data-align={c.align ?? 'start'}
                data-testid={`${testId}-col-${c.key}`}
              >
                {i === 0 ? <span className={s.expanderSlot} aria-hidden /> : null}
                {c.header}
              </div>
            ))}
          </div>
        </div>

        {body.kind === 'ready' || body.kind === 'partial' ? (
          <div role="rowgroup" data-testid={`${testId}-rows`}>
            {body.kind === 'partial' && (
              <p className={s.note} data-kind="partial" role="status">{body.message}</p>
            )}
            {visible.map((v, i) => {
              const row = v.row
              const open = expandedIds.includes(row.id)
              const branch = isBranch(row)
              const selected = isRowSelected(selection, row.id)
              const draggable = mayMove && row.moveable !== false
              const hint = drop?.overId === row.id ? drop : null
              return (
                /* Keyed by id, never by index — see the header comment. */
                <div
                  key={row.id}
                  role="row"
                  className={s.row}
                  style={tracks}
                  data-row-id={row.id}
                  data-testid={`${testId}-row-${row.id}`}
                  data-dragging={dragId === row.id || undefined}
                  data-drop={hint && !hint.invalid ? hint.position : undefined}
                  data-drop-invalid={hint?.invalid ?? undefined}
                  aria-level={v.level}
                  aria-posinset={v.posinset}
                  aria-setsize={v.setsize}
                  aria-rowindex={i + 2}
                  aria-expanded={branch ? open : undefined}
                  aria-selected={onSelectionChange ? selected : undefined}
                  aria-busy={movingId === row.id || undefined}
                  aria-label={row.label}
                  tabIndex={(focusId ?? visible[0]?.row.id) === row.id ? 0 : -1}
                  onFocus={() => setFocusId(row.id)}
                  onKeyDown={(e) => onRowKeyDown(e, v)}
                  draggable={draggable || undefined}
                  onDragStart={draggable ? (e) => {
                    setDragId(row.id)
                    if (e.dataTransfer) {
                      e.dataTransfer.effectAllowed = 'move'
                      e.dataTransfer.setData('text/plain', row.id)
                    }
                  } : undefined}
                  onDragEnd={() => { setDragId(null); setDrop(null) }}
                  onDragOver={(e) => onDragOver(e, v)}
                  onDragLeave={() => setDrop((d) => (d?.overId === row.id ? null : d))}
                  onDrop={(e) => onDropRow(e, v)}
                >
                  {cols.map((c, ci) => (
                    <div
                      key={c.key}
                      role="gridcell"
                      className={ci === 0 ? `${s.cell} ${s.lead}` : s.cell}
                      data-align={c.align ?? 'start'}
                    >
                      {ci === 0 && (
                        <>
                          <span
                            className={s.indent}
                            aria-hidden
                            style={{ width: `${(v.level - 1) * 20}px` }}
                          />
                          {branch ? (
                            <button
                              type="button"
                              className={s.expander}
                              /* The ROW owns `aria-expanded` and the keyboard
                                 path; this is the pointer's copy of the same
                                 act, so it is hidden from the tree and kept out
                                 of the tab order rather than announced twice. */
                              tabIndex={-1}
                              aria-hidden
                              data-testid={`${testId}-expander-${row.id}`}
                              onClick={() => setExpanded(row.id, !open)}
                            >{open ? '▾' : '▸'}
                            </button>
                          ) : <span className={s.expanderSlot} aria-hidden />}
                          {onSelectionChange && (
                            <input
                              type="checkbox"
                              className={s.check}
                              tabIndex={-1}
                              checked={selected}
                              disabled={row.selectable === false}
                              aria-label={`Select ${row.label}`}
                              data-testid={`${testId}-check-${row.id}`}
                              onChange={() => toggleRow(row.id)}
                            />
                          )}
                          {draggable && (
                            <button
                              type="button"
                              className={s.handle}
                              tabIndex={-1}
                              aria-hidden
                              data-testid={`${testId}-handle-${row.id}`}
                              title={`Drag to move ${row.label}, or hold Alt and use the arrow keys`}
                            >⠿
                            </button>
                          )}
                        </>
                      )}
                      <span className={s.label}>{c.cell(row)}</span>
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        ) : (
          <BodyNote body={body} />
        )}
      </div>

      {/* Polite: a completed move is worth hearing at the next natural pause. */}
      <div className={s.sr} role="status" aria-live="polite" data-testid={`${testId}-status`}>
        <span key={status.n}>{status.text}</span>
      </div>
      {/* Assertive: a refusal, a conflict or an error has to interrupt, because
          the person is about to act again believing the first act worked. */}
      <div className={s.sr} role="alert" data-testid={`${testId}-alert`}>
        <span key={alert.n}>{alert.text}</span>
      </div>
    </div>
  )
}

/** The name to say out loud, falling back to the id rather than to nothing. */
function rowLabel<T>(rows: readonly TreeGridRow<T>[], id: string): string {
  return rows.find((r) => r.id === id)?.label ?? id
}

/** The bodiless states, rendered so that none of them is a blank box. */
function BodyNote<T>({ body }: { body: TreeGridBody<T> }) {
  switch (body.kind) {
    case 'loading':
      return <p className={s.note} role="status" data-kind="loading">Loading…</p>
    case 'empty':
      return <p className={s.note} data-kind="empty">{body.message}</p>
    case 'refused':
      /* Not empty and not an error. Nothing is missing; it is withheld, and
         saying which is the difference between governed and broken. */
      return <p className={s.note} role="status" data-kind="refused">{readableError(body.message, REFUSED)}</p>
    case 'error':
      return <p className={s.note} role="alert" data-kind="error">{readableError(body.message)}</p>
    default:
      return null
  }
}
